import { useCallback, useMemo, useState } from 'react';
import * as R from 'ramda';

import { useFileUpload } from '@atom/hooks/useFileUpload';
import { AIChatAttachment } from '@atom/types/ai';
import { ACCEPTED_ATTACHMENT_MIME_TYPES } from '@atom/utilities/aiChatUtilities';

// prefix for files in client memory and not yet created in the back end
const TEMP_FILE_PREFIX = 'temp-';
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

export type UseAIChatAttachmentsResponse = {
  stagedAttachments: AIChatAttachment[];
  stageAttachments: (files: FileList) => Promise<void>;
  getStagedAttachments: () => AIChatAttachment[];
  attachments: Record<string, AIChatAttachment[]>;
  removeStagedAttachment: (mediaId: string) => void;
  clearStagedAttachments: () => void;
  addAttachment: (
    messageId: string,
    sentAttachments: AIChatAttachment[],
  ) => void;
  isUploading: boolean;
};

export const useAIChatAttachments = (
  threadId: string,
): UseAIChatAttachmentsResponse => {
  const [stagedAttachments, setStagedAttachments] = useState<
    AIChatAttachment[]
  >([]);

  const [attachments, setAttachments] = useState<
    Record<string, AIChatAttachment[]>
  >({});

  const [uploadFiles] = useFileUpload();

  const isUploading = stagedAttachments.some(
    attachment => attachment.status === 'uploading',
  );

  const stageAttachments = useCallback(
    async (files: FileList) => {
      const valid = Array.from(files).filter(
        file =>
          ACCEPTED_ATTACHMENT_MIME_TYPES.includes(file.type) &&
          file.size <= MAX_FILE_SIZE,
      );

      const remaining = MAX_ATTACHMENTS - stagedAttachments.length;
      const batch = R.take(remaining, valid);

      if (batch.length === 0) {
        return;
      }

      const pending: AIChatAttachment[] = batch.map(file => ({
        mediaId: `${TEMP_FILE_PREFIX}${Date.now()}-${file.name}`,
        mimeType: file.type,
        fileId: '',
        name: file.name,
        status: 'uploading' as const,
      }));

      setStagedAttachments(prev => [...prev, ...pending]);

      const dataTransfer = new DataTransfer();
      batch.forEach(file => dataTransfer.items.add(file));

      try {
        const items = await uploadFiles({
          files: dataTransfer.files,
          subjectId: threadId,
          subjectType: 'aiChatThread',
          widths: [180, 1440],
        });

        setStagedAttachments(prev => {
          const kept = prev.filter(
            attachment => !attachment.mediaId.startsWith(TEMP_FILE_PREFIX),
          );

          const uploaded: AIChatAttachment[] = items.map(item => ({
            mediaId: item.id,
            mimeType: `image/${item.fileExtension}`,
            fileId: item.fileId,
            name: item.name,
            status: 'ready' as const,
          }));

          return [...kept, ...uploaded];
        });
      } catch {
        setStagedAttachments(prev =>
          prev.map(attachment =>
            attachment.mediaId.startsWith(TEMP_FILE_PREFIX)
              ? { ...attachment, status: 'error' as const }
              : attachment,
          ),
        );
      }
    },
    [stagedAttachments.length, threadId, uploadFiles],
  );

  const removeStagedAttachment = useCallback((mediaId: string) => {
    setStagedAttachments(prev =>
      prev.filter(attachment => attachment.mediaId !== mediaId),
    );
  }, []);

  const clearStagedAttachments = useCallback(() => {
    setStagedAttachments([]);
  }, []);

  const addAttachment = useCallback(
    (messageId: string, sentAttachments: AIChatAttachment[]) => {
      setAttachments(prev => ({
        ...prev,
        [messageId]: sentAttachments,
      }));
    },
    [],
  );

  const getStagedAttachments = useCallback(() => {
    return stagedAttachments;
  }, [stagedAttachments]);

  return useMemo(
    () => ({
      stagedAttachments,
      stageAttachments,
      getStagedAttachments,
      attachments,
      removeStagedAttachment,
      clearStagedAttachments,
      addAttachment,
      isUploading,
    }),
    [
      stagedAttachments,
      stageAttachments,
      getStagedAttachments,
      attachments,
      removeStagedAttachment,
      clearStagedAttachments,
      addAttachment,
      isUploading,
    ],
  );
};
